import type { ErrorEvent, EventHint, Breadcrumb, BreadcrumbHint } from '@sentry/react';

// Sentry tag constants for error categorization
export const SentryTags = {
  ERROR_CATEGORY: 'error-category',
  ERROR_SOURCE: 'error-source',
  NON_ACTIONABLE: 'non-actionable',
  REPLAY_PRIVACY_MODE: 'replay-privacy-mode'
} as const;
export const SentryErrorCategories = {
  BROWSER_INTERNAL: 'browser-internal',
  THIRD_PARTY_SCRIPT: 'third-party-script'
} as const;
export const THIRD_PARTY_SCRIPT_DOMAINS = ['stonly.com', 'googletagmanager.com', 'google-analytics.com', 'maps.googleapis.com', 'mapbox.com'] as const;

// Browser extension/internal errors and library noise that are not actionable
// These come from browser internals injecting variables our code doesn't reference
// Instead of dropping these, we group them with custom fingerprints for visibility
export const BROWSER_INTERNAL_ERROR_PATTERNS: ReadonlyArray<{
  pattern: RegExp;
  fingerprint: string;
  description: string;
}> = [{
  pattern: /__firefox__/i,
  fingerprint: 'browser-internal-firefox',
  description: 'Firefox/Brave internal variables'
}, {
  pattern: /__webkit__/i,
  fingerprint: 'browser-internal-webkit',
  description: 'Safari WebKit internals'
}, {
  pattern: /__gCrWeb/i,
  fingerprint: 'browser-internal-chrome-ios',
  description: 'Chrome on iOS'
}, {
  pattern: /fb_xd_fragment/i,
  fingerprint: 'third-party-facebook-sdk',
  description: 'Facebook SDK'
}, {
  pattern: /instantSearchSDKJSBridgeClearHighlight/i,
  fingerprint: 'browser-internal-ios-search',
  description: 'iOS search highlight'
}, {
  pattern: /Can't find variable: getLocation/i,
  fingerprint: 'browser-extension-ios-safari',
  description: 'iOS Safari extension/keyboard injection'
}, {
  pattern: /window\.ethereum/i,
  fingerprint: 'browser-extension-crypto-wallet',
  description: 'Crypto wallet browser extensions (MetaMask, etc.)'
}, {
  pattern: /feature named .* was not found/i,
  fingerprint: 'browser-internal-duckduckgo',
  description: 'DuckDuckGo browser internal feature flag errors'
}, {
  pattern: /iframe-resizer/i,
  fingerprint: 'library-noise-iframe-resizer',
  description: 'iframe-resizer library upgrade notices (not errors)'
}] as const;

/**
 * Creates the beforeSend hook for Sentry error processing.
 * Handles fingerprinting, categorization, and GDPR data stripping.
 */
export function createBeforeSendHook(enableFullReplay: boolean) {
  return (event: ErrorEvent, _hint: EventHint): ErrorEvent | null => {
    // Note: Sentry automatically links errors to replays when both are running.
    // No manual replay ID attachment needed - Sentry handles this internally.

    // Group browser extension/internal errors with custom fingerprints
    // These are not actionable but we keep them for visibility and trend monitoring
    // They're downgraded to 'warning' level and tagged for easy filtering in Sentry UI
    const errorMessage = event.exception?.values?.[0]?.value || '';
    const matchedBrowserPattern = BROWSER_INTERNAL_ERROR_PATTERNS.find(({
      pattern
    }) => pattern.test(errorMessage));
    if (matchedBrowserPattern) {
      event.fingerprint = [matchedBrowserPattern.fingerprint];
      event.level = 'warning';
      event.tags = {
        ...event.tags,
        [SentryTags.ERROR_CATEGORY]: SentryErrorCategories.BROWSER_INTERNAL,
        [SentryTags.ERROR_SOURCE]: matchedBrowserPattern.description,
        [SentryTags.NON_ACTIONABLE]: 'true'
      };
      // Continue processing - don't return null, we want to capture these grouped
    }

    // Group third-party script errors with custom fingerprints
    // These bypass denyUrls when logged internally by the third-party script
    // We keep them for visibility but group by domain and downgrade severity
    const stacktrace = event.exception?.values?.[0]?.stacktrace?.frames || [];
    const thirdPartyDomain = THIRD_PARTY_SCRIPT_DOMAINS.find(domain => stacktrace.some(frame => frame.filename?.includes(domain)));
    if (thirdPartyDomain) {
      // Create a readable fingerprint from the domain (e.g., 'maps.googleapis.com' -> 'third-party-maps-googleapis')
      const domainFingerprint = `third-party-${thirdPartyDomain.replace(/\./g, '-')}`;
      event.fingerprint = [domainFingerprint];
      event.level = 'warning';
      event.tags = {
        ...event.tags,
        [SentryTags.ERROR_CATEGORY]: SentryErrorCategories.THIRD_PARTY_SCRIPT,
        [SentryTags.ERROR_SOURCE]: thirdPartyDomain,
        [SentryTags.NON_ACTIONABLE]: 'true'
      };
      // Continue processing - don't return null, we want to capture these grouped
    }

    // GDPR: Strip sensitive data based on consent level
    if (!enableFullReplay) {
      // No consent: Minimal PII (strictly necessary for error tracking)
      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers; // May contain auth tokens
      }

      // Keep only user ID for debugging (legitimate interest)
      if (event.user) {
        const userId = event.user.id;
        event.user = {
          id: userId
        };
      }
    } else {
      // Consent granted: More context for better debugging
      // But still strip sensitive auth tokens
      if (event.request?.headers) {
        delete event.request.headers['authorization'];
        delete event.request.headers['cookie'];
      }
    }
    return event;
  };
}

/**
 * Creates the beforeBreadcrumb hook for Sentry breadcrumb filtering.
 * Applies GDPR-compliant URL sanitization based on consent level.
 */
export function createBeforeBreadcrumbHook(enableFullReplay: boolean) {
  return (breadcrumb: Breadcrumb, _hint: BreadcrumbHint): Breadcrumb | null => {
    // GDPR: Different breadcrumb policies based on consent

    if (!enableFullReplay) {
      // No consent: Strict filtering for minimal PII
      if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
        return null;
      }

      // Strip query params from URLs (may contain tokens, emails, etc.)
      if (breadcrumb.data?.url) {
        try {
          const url = new URL(breadcrumb.data.url, window.location.origin);
          breadcrumb.data.url = `${url.origin}${url.pathname}`;
        } catch {
          // Invalid URL, keep as-is
        }
      }
    } else {
      // Consent granted: Allow more breadcrumbs for better debugging
      // Only strip obvious sensitive data (passwords, tokens in URLs)
      if (breadcrumb.data?.url && breadcrumb.data.url.includes('password')) {
        try {
          const url = new URL(breadcrumb.data.url, window.location.origin);
          breadcrumb.data.url = `${url.origin}${url.pathname}`;
        } catch {
          // Invalid URL, keep as-is
        }
      }
    }
    return breadcrumb;
  };
}